import { Component } from '@nicolas-riciotti/light2';
import { normalizeUnicode, levenshtein, maxEdits } from '../../helpers/fuzzy.js';

/* <languages-search> — the v2 Languages block's "do you have mine?" input.

   Reads its languages from the inline <script type="application/json" data-languages>
   that render.php prints (name / native / phrase / aliases), suggests matches under the
   input as you type, and on pick shows the language's phrase as a pill.

   Pressing Enter with nothing highlighted has to choose for the visitor: we take the top
   match when it is the only one, or when it is a name-prefix hit (score >= 85) and the
   runner-up is not. Anything less certain — "ma" hitting Malay, Malayalam, Marathi,
   Macedonian — is treated as no match, and the visitor picks from the list instead.

   There is no submit button. The circle beside the input is a reward: it pops in with a
   drawn tick once a language is confirmed (is-found, styled in languages-search.scss) and
   goes away again as soon as the text is edited.

   Deliberately no update(): this block already does per-frame work for the rotating pills. */

const MAX_SUGGESTIONS = 6;

const PILL_CLASS     = 'inline-block px-20fx py-9fx rounded-4fx text-regular text-5d524b bg-[#ebf6dd] m:px-16px m:py-[6px]';
const PILL_MISS_CLASS = 'inline-block px-20fx py-9fx rounded-4fx text-regular text-5d524b bg-f7ecd9 m:px-16px m:py-[6px]';
const OPTION_CLASS   = 'flex items-baseline gap-10fx px-24fx py-10fx cursor-pointer text-small text-5d524b hover:bg-f7ecd9 aria-selected:bg-f7ecd9 m:px-16px m:py-[8px]';

export default class LanguagesSearch extends Component {

  created() {
    this.onInput    = this.onInput.bind(this);
    this.onKeyDown  = this.onKeyDown.bind(this);
    this.onSubmit   = this.onSubmit.bind(this);
    this.onListDown = this.onListDown.bind(this);
    this.onListClick = this.onListClick.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);

    this._open    = false;
    this._active  = -1;
    this._results = [];
    this._index   = [];
  }

  attached() {
    this.$input  = this.querySelector('[data-input]');
    this.$list   = this.querySelector('[data-list]');
    this.$status = this.querySelector('[data-status]');
    this.$check  = this.querySelector('[data-check]');
    this.$form   = this.querySelector('form');
    this._found  = null; // the row currently confirmed, so edits to the input can clear it

    this._uid     = this.dataset.uid || 'languages-search';
    this._noMatch = this.dataset.noMatch || 'Not yet — but we\'re working on it.';

    let rows = [];
    try {
      rows = JSON.parse(this.querySelector('[data-languages]')?.textContent || '[]');
    } catch (e) {
      rows = [];
    }
    this.buildIndex(Array.isArray(rows) ? rows : []);

    this.$input?.addEventListener('input', this.onInput);
    this.$input?.addEventListener('keydown', this.onKeyDown);
    this.$form?.addEventListener('submit', this.onSubmit);
    this.$list?.addEventListener('pointerdown', this.onListDown);
    this.$list?.addEventListener('click', this.onListClick);
    this.addEventListener('focusout', this.onFocusOut);
  }

  detached() {
    this.$input?.removeEventListener('input', this.onInput);
    this.$input?.removeEventListener('keydown', this.onKeyDown);
    this.$form?.removeEventListener('submit', this.onSubmit);
    this.$list?.removeEventListener('pointerdown', this.onListDown);
    this.$list?.removeEventListener('click', this.onListClick);
    this.removeEventListener('focusout', this.onFocusOut);
  }

  // ---- data -------------------------------------------------------------

  buildIndex(rows) {
    this._index = rows
      .filter(row => row && typeof row.name === 'string' && row.name.trim())
      .map(row => {
        const name = normalizeUnicode(row.name);
        return {
          row,
          name,
          words:   name.split(' ').filter(Boolean),
          native:  normalizeUnicode(row.native || ''),
          aliases: (Array.isArray(row.aliases) ? row.aliases : []).map(normalizeUnicode).filter(Boolean),
        };
      });
  }

  search(query, limit = MAX_SUGGESTIONS) {
    const q = normalizeUnicode(query);
    if (!q) return [];

    const scored = [];
    for (const entry of this._index) {
      let score = 0;

      if (entry.name === q || entry.native === q || entry.aliases.includes(q)) {
        score = 100;
      } else if (entry.name.startsWith(q)) {
        score = 85;
      } else if ((entry.native && entry.native.startsWith(q)) || entry.aliases.some(a => a.startsWith(q))) {
        score = 75;
      } else if (entry.words.some(w => w.startsWith(q))) {
        score = 60;
      } else if (q.length >= 3) {
        // typo tolerance against the start of the name ("portugese", "japanees")
        const target = entry.name.length >= q.length ? entry.name.slice(0, q.length) : entry.name;
        if (levenshtein(q, target) <= maxEdits(q.length)) {
          score = 40;
        }
      }

      if (score) scored.push({ row: entry.row, score });
    }

    scored.sort((a, b) => (b.score - a.score) || a.row.name.localeCompare(b.row.name));
    return scored.slice(0, limit);
  }

  // ---- list -------------------------------------------------------------

  renderList() {
    if (!this.$list) return;
    this.$list.textContent = '';

    this._results.forEach((result, i) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.id = `${this._uid}-opt-${i}`;
      li.dataset.index = String(i);
      li.className = OPTION_CLASS;

      const name = document.createElement('span');
      name.textContent = result.row.name;
      li.appendChild(name);

      if (result.row.native && result.row.native !== result.row.name) {
        const native = document.createElement('span');
        native.className = 'opacity-60';
        native.textContent = result.row.native;
        li.appendChild(native);
      }

      this.$list.appendChild(li);
    });
  }

  open() {
    if (!this.$list || !this._results.length) return;
    this._open = true;
    this.$list.hidden = false;
    this.$input?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this._open = false;
    if (this.$list) this.$list.hidden = true;
    this.$input?.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
  }

  setActive(i) {
    this._active = i;
    const options = this.$list ? this.$list.children : [];
    for (let k = 0; k < options.length; k++) {
      options[k].setAttribute('aria-selected', k === i ? 'true' : 'false');
    }
    if (i >= 0 && options[i]) {
      this.$input?.setAttribute('aria-activedescendant', options[i].id);
      options[i].scrollIntoView({ block: 'nearest' });
    } else {
      this.$input?.removeAttribute('aria-activedescendant');
    }
  }

  // ---- result -----------------------------------------------------------

  pick(row) {
    if (!row) return;
    if (this.$input) this.$input.value = row.name;
    this.close();
    this.showResult(row);
  }

  showResult(row) {
    this._found = row || null;
    if (row) {
      this.showCheck();
    } else {
      this.hideCheck();
    }

    if (!this.$status) return;
    this.$status.textContent = '';

    const pill = document.createElement('span');
    pill.dataset.pill = '';
    pill.className = row ? PILL_CLASS : PILL_MISS_CLASS;
    pill.textContent = row ? (row.phrase || row.name) : this._noMatch;
    this.$status.appendChild(pill);
  }

  clearResult() {
    this._found = null;
    this.hideCheck();
    if (this.$status) this.$status.textContent = '';
  }

  // Re-run the pop even when a language was already showing (French → German).
  showCheck() {
    if (!this.$check) return;
    this.$check.classList.remove('is-found');
    void this.$check.offsetWidth;
    this.$check.classList.add('is-found');
  }

  hideCheck() {
    this.$check?.classList.remove('is-found');
  }

  // ---- events -----------------------------------------------------------

  onInput() {
    // Editing away from the confirmed name takes the tick and the pill with it.
    if (this._found && this.$input.value !== this._found.name) {
      this.clearResult();
    }

    this._results = this.search(this.$input.value);
    this.renderList();
    this.setActive(-1);
    if (this._results.length) {
      this.open();
    } else {
      this.close();
    }
  }

  onKeyDown(e) {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!this._results.length) return;
        e.preventDefault();
        if (!this._open) this.open();
        const n = this._results.length;
        const step = e.key === 'ArrowDown' ? 1 : -1;
        const next = this._active < 0
          ? (step > 0 ? 0 : n - 1)
          : (this._active + step + n) % n;
        this.setActive(next);
        break;
      }
      case 'Enter':
        if (this._open && this._active >= 0) {
          e.preventDefault();
          this.pick(this._results[this._active]?.row);
        }
        // otherwise the form submits and onSubmit decides
        break;
      case 'Escape':
        if (this._open) {
          e.preventDefault();
          this.close();
        } else if (this.$input.value) {
          e.preventDefault();
          this.$input.value = '';
          this._results = [];
          this.clearResult();
        }
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  onSubmit(e) {
    e.preventDefault();
    const query = (this.$input?.value || '').trim();
    if (!query) return;

    const results = this.search(query);
    const top = results[0];
    const runnerUp = results[1];

    const decisive = top && (
      results.length === 1 ||
      (top.score >= 85 && (!runnerUp || runnerUp.score < 85))
    );

    if (decisive) {
      this.pick(top.row);
    } else {
      this.close();
      this.showResult(null);
    }
  }

  // Keep focus in the input while clicking an option, so focusout doesn't close the list first.
  onListDown(e) {
    e.preventDefault();
  }

  onListClick(e) {
    const li = e.target.closest('[role="option"]');
    if (!li) return;
    this.pick(this._results[Number(li.dataset.index)]?.row);
  }

  onFocusOut(e) {
    if (!this.contains(e.relatedTarget)) {
      this.close();
    }
  }
}

customElements.define('languages-search', LanguagesSearch);
